// ==========================================================================
// Squarebit Store - Copyright (c) 2026 Squarebit LLC. All rights reserved.
// Proprietary and confidential. Not for redistribution.
//
// Drawing what the docstring markup meant.
//
// Art direction, continuing the rules in entry.component.tsx:
//
//   AN INLINE LITERAL GETS NO CHIP. There are eight hundred of them, two or
//   three to a sentence. A background behind each one turns a paragraph into
//   grey rubble. It changes face and takes the darkest ink while the prose
//   around it stays a step back - the literal is not lit up, its neighbours
//   are dimmed, which is the same rule as everywhere else on this page.
//
//   TWO LEVELS OF ADMONITION, NOT THREE. A note is a rule and no fill; a
//   warning is the amber already spent on `deprecated`, in a bounded box
//   rather than down the side of an entry. One hazard colour, two scopes.
//   Note against warning reads as a difference in shape, not a traffic light.
//
//   LINKS IN PROSE ARE UNDERLINED, NOT COLOURED. Sixty cross-references set in
//   brand blue would speckle every summary and would stop blue meaning "link"
//   anywhere else on the page. The underline says it is a link; the colour
//   waits for the cursor.
//
// Nothing here is syntax highlighted. Ninety-seven code samples each spending
// five hues would out-colour the reference itself.
// ==========================================================================

import React from 'react';
import { Link } from 'react-router-dom';
import { Block, Inline, parseInline } from './rst';

/** Turns a `:func:`name`` into somewhere to go, or null to leave it as text. */
export type Resolver = (role: string, target: string) => string | null;

const CODE = 'font-mono text-[0.92em] text-content-primary';

const InlineRef: React.FC<{
  node: Inline & { kind: 'ref' };
  resolve?: Resolver;
}> = ({ node, resolve }) => {
  const href = resolve?.(node.role, node.text) ?? null;
  const label = node.text;

  if (!href) {
    // Never fake a link to something we do not host: an underline that leads
    // to a 404 is worse than plain text.
    return <code className={CODE}>{label}</code>;
  }

  const className = `${CODE} no-underline underline decoration-[1px] decoration-border-dark/[0.25] underline-offset-[3px] hover:text-blue hover:decoration-blue transition-colors`;

  return href.startsWith('/') ? (
    <Link to={href} className={className}>
      {label}
    </Link>
  ) : (
    <a href={href} className={className}>
      {label}
    </a>
  );
};

export const InlineNodes: React.FC<{
  nodes: Inline[];
  resolve?: Resolver;
}> = ({ nodes, resolve }) => (
  <>
    {nodes.map((node, index) => {
      if (node.kind === 'code') {
        return (
          <code key={index} className={CODE}>
            {node.text}
          </code>
        );
      }
      if (node.kind === 'strong') {
        return (
          <strong key={index} className="font-semibold text-content-primary">
            <InlineNodes nodes={node.content} resolve={resolve} />
          </strong>
        );
      }
      if (node.kind === 'ref') {
        return <InlineRef key={index} node={node} resolve={resolve} />;
      }
      return <React.Fragment key={index}>{node.text}</React.Fragment>;
    })}
  </>
);

const CodeBlock: React.FC<{ text: string; language: string }> = ({
  text,
  language
}) => (
  // A step quieter than the DocCode used on hand-written pages: this one can
  // sit inside a class rail and a deprecated rule, and needs to read as part
  // of the entry rather than as a page-level object.
  <pre
    className={`mt-[12px] p-[12px] rounded-[8px] bg-surface-section border border-border-dark/[0.11] overflow-x-auto font-mono text-[12px] leading-[1.6] ${
      // Output and shell transcripts are not code you would copy, so they sit
      // a shade back from code you would.
      language === 'text' ? 'text-content-muted-alt' : 'text-content-secondary'
    }`}
  >
    {text}
  </pre>
);

const LABEL: Record<'note' | 'warning', string> = {
  note: 'Note',
  warning: 'Warning'
};

const Admonition: React.FC<{
  level: 'note' | 'warning';
  content: Block[];
  resolve?: Resolver;
}> = ({ level, content, resolve }) =>
  level === 'note' ? (
    <div className="mt-[12px] border-l-2 border-border-dark/[0.14] pl-[14px]">
      <span className="text-[12.5px] font-semibold text-content-muted-alt">
        {LABEL.note}
      </span>
      <div className="mt-[4px]">
        <RstBlocks blocks={content} resolve={resolve} />
      </div>
    </div>
  ) : (
    <div className="mt-[12px] rounded-[8px] bg-warning border border-warning-border p-[12px]">
      <span className="text-[12.5px] font-semibold text-warning-text">
        {LABEL.warning}
      </span>
      <div className="mt-[4px]">
        <RstBlocks blocks={content} resolve={resolve} />
      </div>
    </div>
  );

const BulletList: React.FC<{
  ordered: boolean;
  items: Inline[][];
  resolve?: Resolver;
}> = ({ ordered, items, resolve }) => (
  <ul className="mt-[12px] flex flex-col gap-[6px] list-none p-0 m-0">
    {items.map((item, index) => (
      <li
        key={index}
        className="flex gap-[10px] text-[13.5px] leading-[1.7] text-content-secondary"
      >
        {ordered ? (
          // The numbered chip used on hand-written pages is a step marker and
          // is far too loud repeated inside a reference entry.
          <span className="shrink-0 font-mono text-[12px] text-content-muted-alt">
            {index + 1}.
          </span>
        ) : (
          <span className="shrink-0 mt-[9px] w-[3px] h-[3px] rounded-full bg-border-dark/[0.28]" />
        )}
        <span>
          <InlineNodes nodes={item} resolve={resolve} />
        </span>
      </li>
    ))}
  </ul>
);

/**
 * A run of blocks.
 *
 * Every block carries its gap above and none below, so a note followed by a
 * code sample followed by a list spaces evenly instead of compounding two
 * margins at every seam.
 */
export function RstBlocks({
  blocks,
  resolve,
  size = 'text-[14px] leading-[1.75]',
  tight
}: {
  blocks: Block[];
  resolve?: Resolver;
  /** Type size for prose, so this reads correctly in a summary and in a cell. */
  size?: string;
  /** Drops the leading gap, for the paragraph that opens an entry. */
  tight?: boolean;
}) {
  return (
    <>
      {blocks.map((block, index) => {
        const gap = tight && index === 0 ? '' : 'mt-[12px]';

        if (block.kind === 'para') {
          return (
            <p key={index} className={`${gap} ${size} text-content-secondary`}>
              <InlineNodes nodes={block.content} resolve={resolve} />
            </p>
          );
        }
        if (block.kind === 'code') {
          return (
            <CodeBlock key={index} text={block.text} language={block.language} />
          );
        }
        if (block.kind === 'admonition') {
          return (
            <Admonition
              key={index}
              level={block.level}
              content={block.content}
              resolve={resolve}
            />
          );
        }
        return (
          <BulletList
            key={index}
            ordered={block.ordered}
            items={block.items}
            resolve={resolve}
          />
        );
      })}
    </>
  );
}

/** A single run of inline markup, for a parameter description. */
export const RstText: React.FC<{ text: string; resolve?: Resolver }> = ({
  text,
  resolve
}) => <InlineNodes nodes={parseOnce(text)} resolve={resolve} />;

// Parsing is cheap, but a module page does it a few hundred times on every
// render and the result never changes for a given string.
const cache = new Map<string, Inline[]>();
function parseOnce(text: string): Inline[] {
  const hit = cache.get(text);
  if (hit) return hit;
  const parsed = parseInline(text);
  cache.set(text, parsed);
  return parsed;
}
